/**
 * What an applied organizer run moved, under its tool row: each file with
 * where it came from and went, then the files it wants looked at. Nothing
 * when the run record carries neither (older backends). Folded behind one
 * quiet row ("2 files moved · 1 needs a look") so the transcript doesn't
 * lead with a table; opening it shows the moves and the review list.
 */
import { AlertTriangle, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

import {
  FilesystemAPI,
  type AutocategorizeRunRecord
} from "../../../caseFiles/api/filesystemApi";
import { nodeTitle } from "../../../caseFiles/nodeNames";
import { useCaseDataOptional } from "../../data/CaseDataContext";
import {
  CASE_FILES_ROOT,
  OrganizerMovesTable,
  type OrganizerChange
} from "./OrganizerPlanCard";

const pathLabel = (path: string) => path || CASE_FILES_ROOT;

type Props = { individualId: string; runId: string; className?: string };

export const OrganizerRunMoves: FC<Props> = ({
  individualId,
  runId,
  className
}) => {
  const nodes = useCaseDataOptional()?.fs.nodes;
  const [open, setOpen] = useState(false);
  const [record, setRecord] = useState<AutocategorizeRunRecord | null>(null);

  useEffect(() => {
    let cancelled = false;
    FilesystemAPI.getAutocategorizeRun(individualId, runId)
      .then(({ run }) => {
        if (!cancelled) setRecord(run);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [individualId, runId]);

  const titles = useMemo(
    () => new Map((nodes ?? []).map((n) => [n.id, nodeTitle(n)])),
    [nodes]
  );
  const applied = record?.status === "applied";
  const changes = useMemo<OrganizerChange[]>(
    () =>
      applied
        ? (record?.moves ?? []).map((m) => ({
            nodeId: m.node_id,
            file: titles.get(m.node_id) ?? m.name,
            from: pathLabel(m.from_path),
            to: pathLabel(m.to_path),
            documentType: null
          }))
        : [],
    [applied, record, titles]
  );
  const review = applied ? record?.needs_review ?? [] : [];
  if (!changes.length && !review.length) return null;

  const moved = changes.length
    ? `${changes.length} ${changes.length === 1 ? "file" : "files"} moved`
    : "No files moved";

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn("min-w-0", className)}
      data-testid="organizer-run-moves"
    >
      <CollapsibleTrigger
        className="-ml-1 flex h-(--row-h) min-w-0 items-center gap-1.5 rounded-sm px-1 text-left text-base text-fg-3 hover:bg-fill-1 hover:text-fg"
        data-testid="organizer-run-moves-trigger"
      >
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-fg-4 transition-transform",
            open && "rotate-90"
          )}
        />
        <span>{moved}</span>
        {review.length ? (
          <span className="text-warn">· {review.length} Needs a Look</span>
        ) : null}
      </CollapsibleTrigger>
      <CollapsibleContent className="flex min-w-0 flex-col gap-1.5 pt-0.5">
        <OrganizerMovesTable changes={changes} showFrom />
        {review.length ? (
          <div
            className="flex min-w-0 flex-col gap-0.5 text-sm"
            data-testid="organizer-needs-review"
          >
            <div className="flex items-start gap-2 text-fg-2">
              <AlertTriangle className="mt-0.5 size-3.25 shrink-0 text-warn" />
              <span>
                {review.length}{" "}
                {review.length === 1 ? "file needs" : "files need"} a look
              </span>
            </div>
            <ul className="flex min-w-0 flex-col gap-0.5 pl-5">
              {review.map((r) => (
                <li
                  key={r.node_id}
                  className="min-w-0 text-fg-3"
                  data-testid="organizer-review-item"
                >
                  <span className="text-fg-2">
                    {titles.get(r.node_id) ?? r.name}
                  </span>
                  {r.reason ? `: ${r.reason}` : ""}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
};
